export type AuraEngine = 'mesh' | 'curtain';
export type AuraTextEffect = 'none' | 'shimmer' | 'glow' | 'both';
export type AuraTextGradientMode = 'mono' | 'custom';
export type AuraTheme = 'light' | 'dark';

export interface ThinkingAuraSettings {
  enabled: boolean;
  engine: AuraEngine;
  opacity: number;
  speed: number;
  color1: string;
  color2: string;
  color3: string;
  color4: string;
  lightColor1: string;
  lightColor2: string;
  lightColor3: string;
  lightColor4: string;
  radialEnabled: boolean;
  radialLightColor: string;
  radialDarkColor: string;
  radialOpacity: number;
  radialSize: number;
  radialX: number;
  radialY: number;
  distortion: number;
  swirl: number;
  grain: number;
  waviness: number;
  brightness: number;
  interactive: boolean;
  textEffect: AuraTextEffect;
  textGradientMode: AuraTextGradientMode;
  textGradient1: string;
  textGradient2: string;
  textGradient3: string;
  textBaseOpacity: number;
  textHighlightOpacity: number;
  textSpeed: number;
  textBandWidth: number;
  textFeather: number;
  textGlow: number;
  textTracking: number;
}

export interface ThinkingAuraPreset {
  id: string;
  name: string;
  lightName: string;
  colors: readonly [string, string, string, string];
  lightColors: readonly [string, string, string, string];
}

export const THINKING_AURA_PRESETS: readonly ThinkingAuraPreset[] = [
  { id: 'ember', name: 'Ember bloom', lightName: 'Sky wash', colors: ['#e2712f', '#8b3c70', '#734bb5', '#242245'], lightColors: ['#79bff2', '#8cb8e8', '#a2b6ed', '#d7e8f6'] },
  { id: 'electric', name: 'Electric dusk', lightName: 'Electric air', colors: ['#15d9ff', '#3465ff', '#8b5cf6', '#111637'], lightColors: ['#62c9ed', '#6aa8ef', '#9a9fe9', '#d8e5f6'] },
  { id: 'solar', name: 'Solar flare', lightName: 'Blue dawn', colors: ['#ffcf5a', '#ff7657', '#e846a5', '#391b63'], lightColors: ['#8fc7ed', '#76b6e8', '#a9b7ea', '#e1edf8'] },
  { id: 'mineral', name: 'Mineral tide', lightName: 'Mineral tide', colors: ['#78f6c9', '#21b7ae', '#4966c9', '#142843'], lightColors: ['#83d5db', '#6ac2d6', '#739ee1', '#d5e9f4'] },
  { id: 'orchid', name: 'Orchid ink', lightName: 'Periwinkle', colors: ['#f1a7ff', '#a855f7', '#5936bd', '#211836'], lightColors: ['#a8c9f5', '#8eb5ee', '#9da9e8', '#dce7f7'] },
  { id: 'pearl', name: 'Pearl smoke', lightName: 'Pearl mist', colors: ['#f2dfc7', '#bc8f8f', '#8d6b91', '#302d3d'], lightColors: ['#b8d3e8', '#9ebed9', '#adbad8', '#e3edf5'] },
] as const;

export const DEFAULT_THINKING_AURA: ThinkingAuraSettings = {
  enabled: true,
  engine: 'mesh',
  opacity: 0.46,
  speed: 0.3,
  color1: '#e2712f',
  color2: '#8b3c70',
  color3: '#734bb5',
  color4: '#242245',
  lightColor1: '#79bff2',
  lightColor2: '#8cb8e8',
  lightColor3: '#a2b6ed',
  lightColor4: '#d7e8f6',
  radialEnabled: true,
  radialLightColor: '#ffffff',
  radialDarkColor: '#0f1013',
  radialOpacity: 0.72,
  radialSize: 86,
  radialX: 50,
  radialY: 0,
  distortion: 0.65,
  swirl: 0.25,
  grain: 0.08,
  waviness: 1,
  brightness: 1,
  interactive: false,
  textEffect: 'shimmer',
  textGradientMode: 'mono',
  textGradient1: '#f6e9e1',
  textGradient2: '#ffffff',
  textGradient3: '#f6e9e1',
  textBaseOpacity: 0.5,
  textHighlightOpacity: 0.92,
  textSpeed: 5,
  textBandWidth: 2.8,
  textFeather: 0.9,
  textGlow: 0.45,
  textTracking: 0,
};

export function auraColorsForTheme(settings: ThinkingAuraSettings, theme: AuraTheme): [string, string, string, string] {
  return theme === 'light'
    ? [settings.lightColor1, settings.lightColor2, settings.lightColor3, settings.lightColor4]
    : [settings.color1, settings.color2, settings.color3, settings.color4];
}

export function auraRadialColorForTheme(settings: ThinkingAuraSettings, theme: AuraTheme): string {
  return theme === 'light' ? settings.radialLightColor : settings.radialDarkColor;
}

const STORAGE_KEY = 'viq-thinking-aura-v1';
const LIMITS: Partial<Record<keyof ThinkingAuraSettings, [number, number]>> = {
  opacity: [0, 1], speed: [0, 1.5], radialOpacity: [0, 1], radialSize: [10, 160], radialX: [0, 100], radialY: [0, 100],
  distortion: [0, 1], swirl: [0, 1], grain: [0, 1],
  waviness: [0.2, 2], brightness: [0.3, 2],
  textBaseOpacity: [0.1, 1], textHighlightOpacity: [0.1, 1], textSpeed: [1.5, 8],
  textBandWidth: [0.2, 16], textFeather: [0, 0.9], textGlow: [0, 1], textTracking: [-0.5, 3],
};

export function readThinkingAura(): ThinkingAuraSettings {
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}') as Partial<ThinkingAuraSettings>;
    const result = { ...DEFAULT_THINKING_AURA };
    for (const key of Object.keys(result) as Array<keyof ThinkingAuraSettings>) {
      const value = saved[key];
      if (typeof value === typeof result[key]) (result as unknown as Record<string, unknown>)[key] = value;
    }
    if (!['mesh', 'curtain'].includes(result.engine)) result.engine = DEFAULT_THINKING_AURA.engine;
    if (!['none', 'shimmer', 'glow', 'both'].includes(result.textEffect)) result.textEffect = DEFAULT_THINKING_AURA.textEffect;
    if (!['mono', 'custom'].includes(result.textGradientMode)) result.textGradientMode = DEFAULT_THINKING_AURA.textGradientMode;
    for (const key of ['color1', 'color2', 'color3', 'color4', 'lightColor1', 'lightColor2', 'lightColor3', 'lightColor4', 'radialLightColor', 'radialDarkColor', 'textGradient1', 'textGradient2', 'textGradient3'] as const) {
      if (!/^#[\da-f]{6}$/i.test(result[key])) result[key] = DEFAULT_THINKING_AURA[key];
    }
    for (const [key, [min, max]] of Object.entries(LIMITS) as Array<[keyof ThinkingAuraSettings, [number, number]]>) {
      const value = result[key];
      if (typeof value === 'number') (result as unknown as Record<string, unknown>)[key] = Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : DEFAULT_THINKING_AURA[key];
    }
    return result;
  } catch {
    return { ...DEFAULT_THINKING_AURA };
  }
}

export function saveThinkingAura(settings: ThinkingAuraSettings): void {
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings)); } catch { /* Private browsing can deny storage. */ }
}
